/**
 * Screen 11 — My Bookings. Upcoming, then history.
 *
 * Falls back to the tickets saved on the device when there is no signal, so
 * a member queuing outside the hall can still reach their QR from here and
 * not only from a link they may not have kept.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorNote, Note, Shell } from "../components/Shell";
import type { Locale } from "../i18n";
import { api, isSignedIn, OfflineError, type BookingOut } from "../lib/api";
import { formatDateTime, formatIdr } from "../lib/format";
import { loadTickets, type StoredTicket } from "../lib/tickets";

interface Row {
  ref: string;
  state: string;
  name: string;
  date_time: string;
  seats: string[];
  total_idr: number;
}

const fromBooking = (b: BookingOut): Row => ({
  ref: b.ref,
  state: b.state,
  name: (b.program.text?.name as string) ?? "",
  date_time: b.program.date_time,
  seats: b.seats.map((s) => s.label),
  total_idr: b.total_idr,
});

const fromStored = (ticket: StoredTicket): Row => ({
  ref: ticket.ref,
  state: ticket.state,
  name: ticket.program.name,
  date_time: ticket.program.date_time,
  seats: ticket.seats,
  total_idr: ticket.total_idr,
});

export default function Bookings({ locale }: { locale: Locale }) {
  const { t } = useTranslation();
  const [upcoming, setUpcoming] = useState<Row[] | null>(null);
  const [past, setPast] = useState<Row[]>([]);
  const [offline, setOffline] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSignedIn()) {
      // A guest still has their own tickets on this device.
      const saved = loadTickets().map(fromStored);
      const now = Date.now();
      setUpcoming(saved.filter((r) => new Date(r.date_time).getTime() >= now));
      setPast(saved.filter((r) => new Date(r.date_time).getTime() < now));
      return;
    }
    api
      .myBookings(locale)
      .then((body) => {
        setUpcoming(body.upcoming.map(fromBooking));
        setPast(body.past.map(fromBooking));
        setOffline(false);
      })
      .catch((err) => {
        if (err instanceof OfflineError) {
          const saved = loadTickets().map(fromStored);
          const now = Date.now();
          setUpcoming(saved.filter((r) => new Date(r.date_time).getTime() >= now));
          setPast(saved.filter((r) => new Date(r.date_time).getTime() < now));
          setOffline(true);
        } else {
          setError(t("error.generic"));
        }
      });
  }, [locale, t]);

  const section = (title: string, rows: Row[]) =>
    rows.length > 0 && (
      <>
        <h2 className="section">{title}</h2>
        <ul className="cards">
          {rows.map((row) => (
            <li key={row.ref} className="card">
              <h3 className="card__title">{row.name}</h3>
              <p className="card__meta card__when">{formatDateTime(row.date_time, locale)}</p>
              <p className="card__meta">
                {t("ticket.seat")}: {row.seats.join(", ")}
              </p>
              <p>
                <span className="state" data-state={row.state}>
                  {t(`state.${row.state}`)}
                </span>{" "}
                <span className="money">{formatIdr(row.total_idr)}</span>
              </p>
              {(row.state === "PAID" || row.state === "CHECKED_IN") && (
                <Link className="btn btn--primary" to={`/tickets/${row.ref}`}>
                  {t("ticket.title")}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </>
    );

  return (
    <Shell masthead area="bookings" title={t("bookings.title")}>
      {offline && <Note>{t("error.offline_ticket")}</Note>}
      {error && <ErrorNote>{error}</ErrorNote>}
      {upcoming === null && !error && <Note>{t("common.loading")}</Note>}
      {upcoming?.length === 0 && past.length === 0 && <Note>{t("bookings.none")}</Note>}
      {section(t("bookings.upcoming"), upcoming ?? [])}
      {section(t("bookings.past"), past)}
    </Shell>
  );
}
